<template>
  <!-- The birthday ask as a flow (design 2026-10-26): the ask -> the payoff ->
       the closer look -> the big three. One component in two places:
         context 'gift'   inline in the gift room, the Mystic asking (StageThree)
         context 'altar'  a sheet on a phone / a modal on a desktop, from Altar
                          Home's personalized-horoscope card (PersonalHoroscope)
       Saves through saveBirthMoment; the payoff reads the signs the response
       carries ({moment, signs}). Emits saved(response), done({to}), close. -->
  <component v-if="view !== 'gone'" :is="mode === 'modal' ? BirthSheet : 'div'" v-bind="mode === 'modal' ? { label: sheetLabel } : { class: 'ba-card' }"
             @close="$emit('close')">
    <div class="ba" data-testid="birth-ask">
      <div v-if="view === 'loading'" class="ba-wait">{{ COPY.saving }}</div>

      <BirthMoment v-else-if="view === 'ask'" mode="plain" :fields="['date', 'time', 'place']" :optional="['time', 'place']"
                   :title="askTitle" :subtitle="askWhy" :with-name="withName" :initial="moment" :saving="saving"
                   :server-errors="errors" :closable="mode === 'modal'" :dismiss-label="mode === 'modal' ? COPY.notNow : ''"
                   @save="saveAsk" @close="$emit('close')" />

      <BirthMoment v-else-if="view === 'closer'" :key="'closer'" mode="plain" :fields="['time', 'place']" :optional="['time', 'place']"
                   :title="COPY.closerTitle" :with-name="withName" :initial="moment" :saving="saving" :server-errors="errors"
                   :closable="mode === 'modal'" :dismiss-label="COPY.notNow" @save="saveCloser" @close="notNow" />

      <template v-else>
        <div v-if="mode === 'modal'" class="ba-head">
          <button type="button" class="ba-close" aria-label="Close" @click="$emit('close')">×</button>
        </div>
        <BirthPayoff :signs="result && result.signs" :moment="result && result.moment" :variant="view === 'three' ? 'three' : 'sun'"
                     :opened="opened" :done-label="doneLabel" :actions="!finished" @more="toCloser" @done="finish" />
      </template>
    </div>
  </component>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import BirthPayoff from '@/components/widgets/BirthPayoff.vue'
import BirthSheet from '@/components/widgets/BirthSheet.vue'
import { getBirthMoment, saveBirthMoment, getNav } from '@/api-service'
import { readNav } from '@/utils/nav-stage'
import { store } from '@/store'
import { COPY, momentComplete, openedRows } from '@/utils/birth-ask'

const props = defineProps({
  mode: { type: String, default: 'inline' },        // 'inline' | 'modal'
  context: { type: String, default: 'altar' },      // 'gift' | 'altar'
  // The sender's name in the gift room ("You & Sam").
  withName: { type: String, default: '' },
  // 'ask', or 'closer' (the card's "Add your birth time…" link).
  start: { type: String, default: 'ask' },
  // A moment as GET /birth/moment/ returns it; fetched when missing.
  initial: { type: Object, default: null },
})
const emit = defineEmits(['saved', 'done', 'close'])

const view = ref(props.start === 'closer' && !(props.initial && props.initial.birthdate) ? 'loading' : props.start)
const moment = ref(props.initial)
const result = ref(null)
const saving = ref(false)
const errors = ref(null)
const opened = ref([])
const finished = ref(false)
// Read once, before anything is saved: what this birthday really opens.
const firstBirthday = !(props.initial && props.initial.birthdate) && !store.querent_birthdate
// The Altar's tabs as they stand when the birthday is sent (nav has landed by then).
let tabsBefore = null
// …and as the server has them once the birthday is in: what really opened.
let tabsAfter = null
const readTabs = () => (Array.isArray(store.altar_tabs) ? [...store.altar_tabs] : null)
async function readTabsAfter () {
  try {
    const r = await getNav(store.session_id)
    const n = r && (r.nav || r)
    return n && Array.isArray(n.altar_tabs) ? readNav(n).altarTabs : null
  } catch (e) { return null }
}

const askTitle = computed(() => (props.context === 'gift' ? '' : COPY.sheetTitle))
const askWhy = computed(() => (props.context === 'gift' ? COPY.whyGift(props.withName || 'them') : COPY.whyAltar))
const sheetLabel = computed(() => (view.value === 'closer' ? COPY.closerTitle : COPY.sheetTitle))
const mirrorThere = computed(() => {
  const tabs = tabsAfter || tabsBefore || readTabs()
  return !!(tabs && tabs.includes('mirror')) || opened.value.some((r) => r.key === 'mirror')
})
const doneLabel = computed(() => {
  if (props.context === 'gift') return `See you & ${props.withName || 'them'}`
  return view.value === 'three' && mirrorThere.value ? 'Open my Mirror' : 'Done'
})

onMounted(async () => {
  if (view.value !== 'loading') return
  try { const r = await getBirthMoment(); moment.value = (r && r.moment) || null } catch (e) { moment.value = null }
  view.value = moment.value && moment.value.birthdate ? 'closer' : 'ask'
})

function errorsOf (e) {
  return (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
}

async function save (body) {
  saving.value = true; errors.value = null
  try {
    const r = await saveBirthMoment(body)
    result.value = r || null
    moment.value = (r && r.moment) || { ...(moment.value || {}), ...body }
    store.querent_birthdate = body.birthdate
    if (body.place) {
      store.querent_birth_location_lat = body.place.latitude
      store.querent_birth_location_long = body.place.longitude
    }
    emit('saved', r)
    // The page refreshes nav: a first birthday brings the Mirror tab (nav §13).
    window.dispatchEvent(new CustomEvent('birth-moment-saved', { detail: { first: firstBirthday } }))
    return true
  } catch (e) {
    errors.value = errorsOf(e)
    return false
  } finally { saving.value = false }
}

async function saveAsk (out) {
  const body = { birthdate: out.birthdate, time: out.time || null, ...(out.place ? { place: out.place } : {}) }
  tabsBefore = readTabs()
  if (!(await save(body))) return
  tabsAfter = firstBirthday ? await readTabsAfter() : null
  opened.value = openedRows({ context: props.context, withName: props.withName, firstBirthday, altarTabs: tabsBefore,
    altarTabsAfter: tabsAfter })
  // No signs to show (an old server): the flow goes on as before.
  if (!(result.value && result.value.signs && (result.value.signs.sun || result.value.signs.sun_text))) { view.value = 'gone'; finish(); return }
  view.value = momentComplete(result.value.moment) ? 'three' : 'sun'
}

async function saveCloser (out) {
  // Either one alone is fine; neither is nothing to save.
  if (!out.time && !out.place) return
  const birthdate = (moment.value && moment.value.birthdate) || out.birthdate
  const body = { birthdate, time: out.time || null, ...(out.place ? { place: out.place } : {}) }
  if (!(await save(body))) return
  if (!(result.value && result.value.signs)) { view.value = 'gone'; finish(); return }
  view.value = 'three'
}

function toCloser () { errors.value = null; view.value = 'closer' }
// "Not now" on the closer look: back to the payoff, or (opened from the card) closed.
function notNow () {
  errors.value = null
  if (result.value && result.value.signs) view.value = momentComplete(result.value.moment) ? 'three' : 'sun'
  else emit('close')
}
function finish () {
  const to = props.context === 'altar' && view.value === 'three' && mirrorThere.value ? 'mirror' : ''
  if (props.context === 'gift') finished.value = true
  emit('done', { to, result: result.value })
}
</script>

<style scoped>
.ba-card { background: rgba(20, 7, 31, 0.94); border: 1px solid rgba(240, 217, 163, 0.22); border-radius: 16px; padding: 16px; box-sizing: border-box; }
.ba { width: 100%; }
.ba-wait { color: #9b86c9; font-family: 'Letter Serif', Georgia, serif; font-size: 17px; padding: 12px 0; text-align: center; }
.ba-head { display: flex; justify-content: flex-end; margin-bottom: -18px; position: relative; z-index: 1; }
.ba-close { background: none; border: 0; color: #9b86c9; font-size: 26px; line-height: 1; cursor: pointer; min-width: 44px; min-height: 44px; margin: -8px -10px 0 0; }
@media (max-width: 600px) {
  .ba-card { padding: 14px; }
}
</style>
